import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@/components/Button';
import { InputField } from '@/components/InputFields';
import VerificationCodeInput from '@/components/VerificationCode';
import TextLink from '@/components/TextLink';
import useApi from '@/hooks/use-api';
import useErrorHandler from '@/hooks/use-error-handler';
import usePlatform from '@/hooks/use-platform';
import useTextHandler from '@/hooks/use-text-handler';
import { createTotpSecret } from '@/apis/experience';
import { completeVerificationStep } from '@/apis/experience/verification-setup';

import type { VerificationStepProps } from '../types';

import styles from './TotpSetupStep.module.scss';

const TotpSetupStep = ({ step, onStepComplete, progress }: VerificationStepProps) => {
  const { t } = useTranslation();
  const { isMobile } = usePlatform();
  const { copyText } = useTextHandler();
  const handleError = useErrorHandler();
  
  const [secret, setSecret] = useState<string>('');
  const [secretQrCode, setSecretQrCode] = useState<string>('');
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState<string[]>([]);
  const [isQrCodeFormat, setIsQrCodeFormat] = useState(!isMobile);
  const [isSecretGenerated, setIsSecretGenerated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const asyncCreateSecret = useApi(createTotpSecret);
  const asyncCompleteStep = useApi(completeVerificationStep);

  // Generate TOTP secret when component mounts
  useEffect(() => {
    if (step.status === 'in-progress' && !isSecretGenerated) {
      const generateSecret = async () => {
        try {
          setIsSubmitting(true);
          
          const [error, result] = await asyncCreateSecret();

          if (error) {
            await handleError(error);
            return;
          }

          if (result) {
            setSecret(result.secret);
            setSecretQrCode(result.secretQrCode);
            setVerificationId(result.verificationId);
            setIsSecretGenerated(true);
          }
        } catch (error) {
          await handleError(error);
        } finally {
          setIsSubmitting(false);
        }
      };

      void generateSecret();
    }
  }, [step.status, isSecretGenerated, asyncCreateSecret, handleError]);

  // Verify TOTP code and complete step
  const handleVerifyTotp = useCallback(async () => {
    const codeString = verificationCode.join('');
    if (!codeString || codeString.length !== 6 || !verificationId) return;

    try {
      setIsSubmitting(true);
      
      const [error, result] = await asyncCompleteStep('totp', {
        verificationId,
        code: codeString,
      });

      if (error) {
        await handleError(error);
        return;
      }

      if (result?.success) {
        await onStepComplete('totp');
      }
    } catch (error) {
      await handleError(error);
    } finally {
      setIsSubmitting(false);
    }
  }, [verificationCode, verificationId, asyncCompleteStep, handleError, onStepComplete]);

  if (step.status === 'completed') {
    return (
      <div className={styles.completedStep}>
        <div className={styles.completedIcon}>✅</div>
        <div className={styles.completedContent}>
          <h3>Authenticator App Setup Complete</h3>
          <p>Your authenticator app is now configured and ready to use for secure authentication</p>
          {progress?.totpSetupAt && (
            <div className={styles.completedTime}>
              Completed at {new Date(progress.totpSetupAt).toLocaleString()}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (step.status === 'pending') {
    return (
      <div className={styles.pendingStep}>
        <div className={styles.pendingIcon}>⏳</div>
        <div className={styles.pendingContent}>
          <h3>Authenticator App Setup</h3>
          <p>Complete the previous steps first to unlock this step</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.stepContainer}>
      <div className={styles.stepHeader}>
        <div className={styles.stepIcon}>{step.icon}</div>
        <div>
          <h2 className={styles.stepTitle}>{step.title}</h2>
          <p className={styles.stepDescription}>{step.description}</p>
        </div>
      </div>

      <div className={styles.stepContent}>
        {!isSecretGenerated ? (
          // Loading secret generation
          <div className={styles.loadingSection}>
            <p className={styles.instruction}>
              Generating authenticator setup...
            </p>
          </div>
        ) : (
          <div className={styles.setupSection}>
            {/* Step 1: Setup authenticator app */}
            <div className={styles.secretSection}>
              <h4 className={styles.sectionTitle}>
                Step 1: Set up your authenticator app
              </h4>
              <p className={styles.instruction}>
                {isQrCodeFormat ? 'Scan the QR code with your authenticator app' : 'Copy and paste this key into your authenticator app'}
              </p>

              <div className={styles.secretContent}>
                {isQrCodeFormat && secretQrCode ? (
                  <div className={styles.qrCode}>
                    <img src={secretQrCode} alt="QR code" className={styles.qrCodeImage} />
                  </div>
                ) : (
                  <div className={styles.copySecret}>
                    <div className={styles.rawSecret}>{secret}</div>
                    <Button
                      title="Copy Key"
                      type="secondary"
                      onClick={() => {
                        void copyText(secret, 'Secret key copied!');
                      }}
                    />
                  </div>
                )}

                <TextLink
                  text={isQrCodeFormat ? 'Can\'t scan? Use manual entry' : 'Want to scan QR code instead?'}
                  onClick={() => {
                    setIsQrCodeFormat(!isQrCodeFormat);
                  }}
                />
              </div>
            </div>

            {/* Step 2: Verify setup with code */}
            <div className={styles.verificationSection}>
              <h4 className={styles.sectionTitle}>
                Step 2: Verify your setup
              </h4>
              <p className={styles.instruction}>
                Enter the 6-digit code from your authenticator app to complete setup
              </p>

              <div className={styles.codeInputSection}>
                <VerificationCodeInput
                  name="verificationCode"
                  value={verificationCode}
                  onChange={(code) => setVerificationCode(code)}
                  className={styles.codeInput}
                />
              </div>

              <Button
                title="Complete Authenticator Setup"
                type="primary"
                size="large"
                isLoading={isSubmitting}
                isDisabled={verificationCode.join('').length !== 6}
                onClick={handleVerifyTotp}
              />
            </div>
          </div>
        )}
      </div>

      <div className={styles.stepFooter}>
        <div className={styles.helpText}>
          Download an authenticator app like Google Authenticator or Authy. Scan the QR code or manually enter the secret key to add your account. The app will generate 6-digit codes that refresh every 30 seconds.
        </div>
      </div>
    </div>
  );
};

export default TotpSetupStep;